import React, { useEffect, useMemo, useState } from 'react';
import { MuiAutocompleteSelect, ProjectDialog, PillButton, GoogleSignInButton, IGoogleLoginSuccess, IGoogleLoginError, CustomTooltip, ConfirmationDialog } from '@zz2/zz2-ui';
import { useAppDispatch } from '../../@types/redux';
import { IUser } from '../../@types/model/auth/user/user';
import { getMasterDataLastSyncDateLocalStorage, getUserSelectedDepartmentsLocalStorage, getUserSelectedDivisionsLocalStorage, setUserSelectedDepartmentsLocalStorage, setUserSelectedDivisionsLocalStorage } from '../../service/localStorageService';
import DataActions from '../../store/data/actions';
import moment from 'moment';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import GeneralThunk from '../../store/general/thunk';
import DataThunks from '../../store/data/thunk';
import { OptionType } from '../../@types/model/optionType';
import { IOptionType } from '@zz2/zz2-ui';
import { useGetDivisions } from '../../hooks/query/masterData/divisionQueries';
import { useGetDepartments } from '../../hooks/query/masterData/departmentQueries';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useHandleLinkGoogleAccount, useHandleUnlinkGoogleAccount } from '../../hooks/query/rights/userQueries';
import { ClearIcon } from '@mui/x-date-pickers';
import FreshServiceActions from '../../store/freshService/actions';

interface IUserSettingsProps {
    isOpen : boolean;
    setIsUserDialogOpenCallback : (state : boolean) => void;
    currentUser : IUser | null;
    onClose : () => void;
    signOut : () => void;
}

const UserSettings = (props : IUserSettingsProps) : React.ReactElement => {
    const dispatch = useAppDispatch();

    const selectedDivisions = getUserSelectedDivisionsLocalStorage();
    const selectedDepartments = getUserSelectedDepartmentsLocalStorage();

    const [selectedDivisionOptions, setSelectedDivisionOptions] = useState<Array<IOptionType>>([]);
    const [selectedDepartmentOptions, setSelectedDepartmentOptions] = useState<Array<IOptionType>>([]);
    const [isUnlinkGoogleAccountDialogOpen, setIsUnlinkGoogleAccountDialogOpen] = useState<boolean>(false);
    const [logOutUserDialogOpen, setLogOutUserDialogOpen] = useState<boolean>(false);
    const [resyncMasterDataDialogOpen, setResyncMasterDataDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    const { isLoading: isLoadingDivisions, data: divisions } = useGetDivisions({ divisionIds: null });
    const { isLoading: isLoadingDepartments, data: departments } = useGetDepartments({ departmentIds: null, subdivisionId: null});
    const { isLoading: isUnlinkingGoogleAccount, mutate: unlinkGoogleAccount } = useHandleUnlinkGoogleAccount();
    const { isLoading: isLinkingGoogleAccount, mutate: linkGoogleAccount } = useHandleLinkGoogleAccount();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (props.currentUser) {
            loadData();
        }
    }, [props.isOpen, divisions, departments]);


    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    
    const loadData = () : void => {
        if (props.currentUser?.divisionIds && props.currentUser.divisionIds.length > 0) {
            const selectedDivisionOptions = divisions?.filter(div => selectedDivisions.some(localSelectedDiv => div.id === localSelectedDiv.value))
                .map(OptionType.fromDataModel) ?? [];

            if (selectedDivisions.length < 1) return;

            setSelectedDivisionOptions(selectedDivisionOptions);
            dispatch(DataActions.setSelectedUserDivisionIds(selectedDivisions.map(x => Number(x.value))));
        } else {
            setUserSelectedDivisionsLocalStorage([]);
            dispatch(DataActions.setSelectedUserDivisionIds(null));
        }

        if (props.currentUser?.departmentIds && props.currentUser.departmentIds.length > 0) {
            const selectedDepartmentOptions = departments?.filter(dept => selectedDepartments.some(localSelectedDept => dept.id === localSelectedDept.value))
                .map(OptionType.fromDataModel) ?? [];

            if (selectedDepartments.length < 1) return;

            setSelectedDepartmentOptions(selectedDepartmentOptions);
            dispatch(DataActions.setSelectedUserDepartmentIds(selectedDepartments.map(x => Number(x.value))));
        } else {
            setUserSelectedDepartmentsLocalStorage([]);
            dispatch(DataActions.setSelectedUserDepartmentIds(null));
        }
    };

    const onGoogleAccountLinkSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        linkGoogleAccount({ code: response.code });
    };

    const onGoogleAccountLinkFailure = (response : IGoogleLoginError) : void => {
        dispatch(GeneralThunk.showErrorSnackbar({
            defaultMessage: response.error ?? 'Google Account linking failed.',
            ex: response,
        }));
    };

    const onUnlinkGoogleAccount = () : void => {
        if (!props.currentUser)
            return;

        unlinkGoogleAccount();
        closeUnlinkGoogleAccountDialog();
    };


    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/


    const onDivisionChange = (selectedOptions : Array<IOptionType>) : void => {
        setSelectedDivisionOptions(selectedOptions);
        setSelectedDepartmentOptions([]);
    };

    const onDepartmentChange = (selectedOptions : Array<IOptionType>) : void => {
        setSelectedDepartmentOptions(selectedOptions);
    };

    const onDivisionSelectAll = () : void => {
        if (divisionOptions.length !== selectedDivisionOptions.length) {
            onDivisionChange(divisionOptions);
        } else {
            onDivisionChange([]);
            onDepartmentChange([]);
        }
    };

    const onDepartmentSelectAll = () : void => {
        if (departmentOptions.length !== selectedDepartmentOptions.length) {
            onDepartmentChange(departmentOptions);
        } else {
            onDepartmentChange([]);
        }
    };

    const onMasterDataResyncClick = async () : Promise<void> => {
        if (!props.isOpen) return;
        props.setIsUserDialogOpenCallback(false);
        dispatch(DataThunks.manualSyncAllMasterData());
    };

    const onCloseUserSettings = () : void => {
        if (selectedDivisionOptions.length < 1 || selectedDepartmentOptions.length < 1) {
            dispatch(GeneralThunk.showErrorSnackbar({ defaultMessage: 'Select at least one division and department.' }));
            return;
        }

        setUserSelectedDivisionsLocalStorage(selectedDivisionOptions);
        dispatch(DataActions.setSelectedUserDivisionIds(selectedDivisionOptions.map(x => Number(x.value))));
        setUserSelectedDepartmentsLocalStorage(selectedDepartmentOptions);
        dispatch(DataActions.setSelectedUserDepartmentIds(selectedDepartmentOptions.map(x => Number(x.value))));
        props.onClose();
    };

    const getMasterDataLastSyncDate = () : string => {
        const lastSyncDate : moment.Moment | null = getMasterDataLastSyncDateLocalStorage();

        return lastSyncDate ? moment(lastSyncDate).format('DD/MM/YYYY HH:mm:ss') : '';
    };

    const onLogOutUserClick = () : void => {
        props.signOut();
        closeLogOutUserDialog();
    };

    const openUnlinkEmailDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(true);

    const closeUnlinkGoogleAccountDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(false);

    const openLogOutUserDialog = () : void => setLogOutUserDialogOpen(true);

    const closeLogOutUserDialog = () : void => setLogOutUserDialogOpen(false);

    const openResyncMasterDataDialog = () : void => setResyncMasterDataDialogOpen(true);

    const closeResyncMasterDataDialog = () : void => setResyncMasterDataDialogOpen(false);

    const requestAccess = (field : 'divisions' | 'departments') : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
        dispatch(FreshServiceActions.setSupportTicketDescription(`Requesting access to assigned ${field}.`));
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLinkingGoogleAccount
        || isUnlinkingGoogleAccount
        || isLoadingDivisions
        || isLoadingDepartments;
    }, [
        isLinkingGoogleAccount,
        isUnlinkingGoogleAccount,
        isLoadingDivisions,
        isLoadingDepartments
    ]);


    const divisionOptions = useMemo<Array<IOptionType>>(() => {
        if (!divisions || !props.currentUser || !props.currentUser.divisionIds) return [];

        return divisions
            .filter(div => div.isActive && (props.currentUser?.divisionIds?.some(divId => div.id === divId)))
            .map(OptionType.fromDataModel);
    }, [divisions, props.currentUser]);

    const departmentOptions = useMemo<Array<IOptionType>>(() => {
        if (!departments || !props.currentUser || !props.currentUser.departmentIds) return [];

        const selectedDivisionIds = selectedDivisionOptions.map(x => x.value);

        return departments
            .filter(dept => dept.isActive
                && props.currentUser?.departmentIds?.includes(dept.id)
                && selectedDivisionIds.includes(dept.subdivision?.divisionId ?? 0))
            .map(OptionType.fromDataModel);
    }, [departments, props.currentUser, selectedDivisionOptions]);


    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const renderDivisionSelection = useMemo(() => {
        if (!props.currentUser) return <div></div>;
        return (
            (!props.currentUser.divisionIds || props.currentUser.divisionIds.length < 1)
                ? <Button className={' mt30 w300 '} variant='contained' onClick={() : void => requestAccess('divisions')}>
                    {'Request divisions access'}
                </Button>
                : <><MuiAutocompleteSelect
                    isMulti
                    className={'w300 mt30 mxh200 oya'}
                    name={'divisions'}
                    label={'Divisions'}
                    options={divisionOptions}
                    value={selectedDivisionOptions}
                    onChange={onDivisionChange} />
                <Button size={'small'} onClick={onDivisionSelectAll} disabled={divisionOptions.length < 1}>
                    {(divisionOptions.length > 0 && divisionOptions.length === selectedDivisionOptions.length) ? 'Unselect All' : 'Select All'}
                </Button></>
        );
    }, [props.currentUser, divisionOptions, selectedDivisionOptions]);

    const renderDepartmentSelection = useMemo(() => {
        if (!props.currentUser) return <div></div>;
        return (
            (!props.currentUser.departmentIds || props.currentUser.departmentIds.length < 1)
                ? <Button className={' mt30 w300 '} variant='contained' onClick={() : void => requestAccess('departments')}>
                    {'Request departments access'}
                </Button>
                : <><MuiAutocompleteSelect
                    isMulti
                    className={'w300 mt30 mxh200 oya'}
                    name={'departments'}
                    label={'Departments'}
                    options={departmentOptions}
                    value={selectedDepartmentOptions}
                    onChange={onDepartmentChange}
                />
                <Button size={'small'} onClick={onDepartmentSelectAll} disabled={departmentOptions.length < 1}>
                    {(departmentOptions.length > 0 && departmentOptions.length === selectedDepartmentOptions.length) ? 'Unselect All' : 'Select All'}
                </Button></>
        );
    }, [props.currentUser, departmentOptions, selectedDepartmentOptions]);

    return (
        <ProjectDialog
            title={'User Settings'}
            isOpen={props.isOpen}
            fullWidth
            maxWidth={'xs'}
            isLoadingCircular={isLoading}
            onClose={onCloseUserSettings}>
            <div className={'flx1 fdc aic jcc m10'}>
                {renderDivisionSelection}
                {renderDepartmentSelection}
                <div className={'fdc mt30 mb10 aic jcc'}>
                    <Typography className={'mb5'} variant={'body2'}>{`Last sync date: ${getMasterDataLastSyncDate()}`}</Typography>
                    <Tooltip className='mt5 aic jcc' title={'Does a full resyncing of the master data'}>
                        <Button variant={'contained'} className={' w300 '} onClick={openResyncMasterDataDialog}>Resync Master Data</Button>
                    </Tooltip>
                </div>
                <div className={'fdc aic'}>
                    {
                        (!props.currentUser?.email || !props.currentUser.profilePictureURL)
                            && <div className={'zi1'}> 
                                <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
                                    <GoogleSignInButton          
                                        onGoogleSignInSuccess={onGoogleAccountLinkSuccess}
                                        onGoogleSignInFailure={onGoogleAccountLinkFailure}   
                                        buttonText={`Link ${(!!props.currentUser?.email && !props.currentUser.profilePictureURL) ? 'Profile Picture' : 'Google Account'}`}   
                                    />
                                </GoogleOAuthProvider>
                            </div>
                    }
                    {
                        !!props.currentUser?.email
                        && <div className={'fdc aic jcc mb10'}>
                            <div className={'fdr aic jcc'}>
                                <Typography className={'fdr m10'}>Linked Email:</Typography>
                                <Typography variant={'body1'} className={'fdr fwb cpd m10'}>{ props.currentUser.email }</Typography>
                                <CustomTooltip title={'Unlink Email'}>
                                    <IconButton className={'cr'} onClick={openUnlinkEmailDialog}>
                                        <ClearIcon />
                                    </IconButton>
                                </CustomTooltip>
                            </div>
                        </div>
                    }
                </div>
                <div className={'mt20 fdr aic pb10'}>
                    <PillButton
                        className={'mr5 h35 w150'}
                        text={'LOGOUT'}
                        color={'secondary'}
                        onClick={openLogOutUserDialog}
                    />
                    <PillButton
                        className={'ml5 h35 w150'}
                        text={'CLOSE'}
                        color={'default'}
                        onClick={onCloseUserSettings}
                    />
                </div>
            </div>
            <ConfirmationDialog 
                open={logOutUserDialogOpen}
                title={'Log Out?'}
                description={'Are you sure you would like to logout?'}
                onAccept={onLogOutUserClick}
                onClose={closeLogOutUserDialog}
                dialogType={'red'}
            />
            <ConfirmationDialog 
                open={resyncMasterDataDialogOpen}
                title={'Resync Master Data'}
                description={'Resyncing master data may take a while. Do you want to proceed?'}
                onAccept={onMasterDataResyncClick}
                onClose={closeResyncMasterDataDialog}
                dialogType={'orange'}
            />
            <ConfirmationDialog 
                open={isUnlinkGoogleAccountDialogOpen}
                title={'Unlink Email'}
                description={'Are you sure you would like to unlink the email address currently associated with your account?'}
                onAccept={onUnlinkGoogleAccount}
                onClose={closeUnlinkGoogleAccountDialog}
                dialogType={'orange'}
            />
        </ProjectDialog >
    );
};

export default UserSettings;